import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { IoSendSharp } from "react-icons/io5";
import { FaShoppingCart } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import Footer from "./Footer";
import Header from "./Header";
import Scroll from "./Scroll";
import { useNavigate } from "react-router-dom";
import CrackersLoader from './CrackersLoader';
import Sticker from "./Sticker";
import MobileNavigation from "./MobileNavigation";
import FloatingCart from "./FloatingCart";
import "./home.css";

// Section components
import CarouselBanner from "./sections/CarouselBanner";
import MarqueeBar from "./sections/MarqueeBar";
import CategoriesSection from "./sections/CategoriesSection";
import BrandsSection from "./sections/BrandsSection";
import NewArrivalsSection from "./sections/NewArrivalsSection";
import CategoryProductsSection from "./sections/CategoryProductsSection";
import FireworksEffect from "../Components/FireworksEffect";

const Home = () => {
  const [isChatBoxVisible, setChatBoxVisible] = useState(false);
  const [categories, setCategories] = useState([]);
  const [carouselImages, setCarouselImages] = useState([]);
  const [marqueeText, setMarqueeText] = useState("");
  const [message, setMessage] = useState("");
  const [homeData, setHomeData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [gallery, setGallery] = useState([]);
  const [clientlogo, setClientLogo] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [showFloatingCart, setShowFloatingCart] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [discountvalue, setDiscountvalue] = useState(0);
  const [counts, setCounts] = useState({});
  const navigate = useNavigate();
  const observerRef = useRef(null);

  const toggleChatBox = () => setChatBoxVisible(!isChatBoxVisible);
  const handleMessageChange = (e) => setMessage(e.target.value);
  const sendMessage = () => {
    if (!whatsappNumber) return;
    const whatsappUrl = `https://wa.me/${whatsappNumber.replace("+", "")}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  // Toggle floating cart
  const toggleFloatingCart = () => {
    setShowFloatingCart(!showFloatingCart);
  };

  // Get total count of UNIQUE products in cart
  const getTotalItems = () => {
    return Object.keys(counts).length;
  };

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Fetch ALL categories from category_list.php (for the categories section)
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.post(`${API_BASE_URL}category_list.php`);
        
        if (response.data && response.data.head && response.data.head.code === 200) {
          const categoriesData = response.data.body || [];
          setCategories(categoriesData);
          
          if (categoriesData.length > 0) {
            setSelectedCategoryId(categoriesData[0].category_id);
          }
        } else {
          console.error("Failed to fetch categories:", response.data);
          setCategories([]);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        setCategories([]);
      }
    };
    fetchCategories();
  }, []);

  // Fetch banners, marquee, gallery, client logos from home.php
  useEffect(() => {
    const fetchHomeData = async () => {
      try {
        const response = await axios.post(`${API_BASE_URL}home.php`);
        const { body } = response.data;
        setCarouselImages(body?.banner || []);
        setMarqueeText(body?.scrollnew?.map(item => item.msg).join(" | ") || "");
        setGallery(body?.gallery || []);
        setClientLogo(body?.client_logo || []);
      } catch (error) {
        console.error("Error fetching home data:", error);
      }
    };
    fetchHomeData();
  }, []);

  // Fetch home content & WhatsApp number
  useEffect(() => {
    const fetchHomeData = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}content.php`);
        const { body } = response.data;
        const { whatsapp_number } = body.contact;
        setHomeData(body.home);
        setWhatsappNumber(whatsapp_number);
        
        // Get discount value from settings
        if (body.discount_value && body.discount_value.length > 0) {
          setDiscountvalue(body.discount_value[0]?.discount_value || 0);
        }
      } catch (error) {
        console.error("Error fetching content:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchHomeData();
  }, []);

  // Load cart items from localStorage
  useEffect(() => {
    const loadCartItems = () => {
      const items = JSON.parse(localStorage.getItem("cartItems")) || [];
      setCartItems(items);
      
      // Load counts
      const savedCounts = {};
      items.forEach(item => {
        savedCounts[item.product_id] = item.count;
      });
      setCounts(savedCounts);
      
      const count = parseInt(localStorage.getItem("cartCount")) || 0;
      setCartCount(count);
    };
    loadCartItems();

    const handleCartUpdate = () => {
      loadCartItems();
    };
    window.addEventListener("cartUpdated", handleCartUpdate);
    window.addEventListener("storage", handleCartUpdate);

    return () => {
      window.removeEventListener("cartUpdated", handleCartUpdate);
      window.removeEventListener("storage", handleCartUpdate);
    };
  }, []);

  // Intersection Observer
  useEffect(() => {
    if (isLoading || !homeData) return;

    const timer = setTimeout(() => {
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("visible");
              observerRef.current?.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1, rootMargin: "0px 0px -30px 0px" }
      );

      document.querySelectorAll(".fade-in-up").forEach((el) => {
        observerRef.current?.observe(el);
      });
    }, 200);

    return () => {
      clearTimeout(timer);
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [isLoading, homeData]);

  const handleCategoryClick = (category) => {
    navigate('/quick', { state: { selectedCategory: category.category_name } });
  };

  if (isLoading) return <CrackersLoader />;
  if (!homeData) return <p></p>;

  return (
    <div className="home-main-container">
      <Header />
      <CarouselBanner images={carouselImages}>
        <FireworksEffect />
      </CarouselBanner>
      <MarqueeBar text={marqueeText} />
      <Sticker />
      
      {/* Categories Section - Shows all active categories */}
      <CategoriesSection 
        categories={categories} 
        onCategoryClick={handleCategoryClick} 
      />
      
      {/* New Arrivals Section - Navigates to /productdetail/:id */}
      <NewArrivalsSection categoryId={selectedCategoryId} />
      
      {/* Category Products Section - Navigates to /productdetail/:id */}
      <CategoryProductsSection />
      
      <BrandsSection logos={clientlogo} />

      {/* Cart Floating Icon */}
      {getTotalItems() > 0 && (
        <div className="cart-floating-icon-home" onClick={toggleFloatingCart}>
          <span className="cart-icon-badge-home">{getTotalItems()}</span>
          <FaShoppingCart className="cart-icon-home" />
        </div>
      )}

      {/* Floating Cart Sidebar */}
      <FloatingCart 
        isOpen={showFloatingCart}
        onClose={toggleFloatingCart}
        cartItems={cartItems}
        discountvalue={discountvalue}
        onUpdateCart={setCartItems}
        onCountsUpdate={setCounts}
      />

      {/* WhatsApp Floating Button */}
      <div className="floating-whatsapp-container">
        <a
          href={`https://wa.me/${whatsappNumber}`}
          target="_blank"
          rel="noopener noreferrer"
          className="floating-whatsapp"
        >
          <FontAwesomeIcon icon={faWhatsapp} />
        </a>
        {isChatBoxVisible && (
          <div className="chat-box">
            <div className="wats-header">
              <p>My Crackers</p>
              <button className="close-button" onClick={toggleChatBox}>X</button>
            </div>
            <div className="wats-content">
              <p>Welcome To My Crackers</p>
            </div>
            <div className="chat-content">
              <textarea 
                placeholder="Type your message here..." 
                value={message} 
                onChange={handleMessageChange}
              ></textarea>
              <button onClick={sendMessage}><IoSendSharp /></button>
            </div>
          </div>
        )}
      </div>

      <Scroll />
      <Footer />
      <MobileNavigation />
    </div>
  );
};

export default Home;